"use client";

const accent = "text-[#ff5c00]";
const asteriskClassName = `text-4xl font-medium leading-none md:text-5xl ${accent}`;

type TopServiceCard = {
  title: string;
  paragraphs: string[];
};

const topRowServices: TopServiceCard[] = [
  {
    title: "Reband",
    paragraphs: [
      "Brand design is where strategy becomes visible. It's the moment your brand moves from ideas into something people can see, recognise, and connect with.",
      "Strong design does more than look good. It builds trust and shapes how your audience experiences your business from the first interaction.",
      "At Hi Ho, we combine creativity with strategic thinking to translate brand strategy into distinctive visual systems that capture attention, strengthen recognition, and ensure your brand is experienced consistently wherever your audience encounters it.",
    ],
  },
  {
    title: "Ideation and Concepting",
    paragraphs: [
      "Ideas are where brands begin to take shape. The right concept doesn't just capture attention, it communicates personality, purpose, and the ethos behind your business.",
      "Strong creative thinking helps translate brand values into concepts that feel authentic and memorable. When ideas are clear and connected to strategy, they create a consistent narrative that your audience can recognise and relate to.",
      "At Hi Ho, we specialise in developing creative concepts that express the true character of your brand. By blending strategic thinking with imaginative execution, we create ideas that resonate with your audience, strengthen your identity, and set your brand apart in meaningful ways.",
    ],
  },
  {
    title: "UI and UX Design",
    paragraphs: [
      "UI and UX design shape how customers experience your brand online. A well-designed interface guides users clearly through your website, making interactions simple and intuitive.",
      "Strong UX design removes friction from the customer journey. By understanding how people search and make decisions, we design pathways that help visitors find what they need quickly and move confidently toward action.",
      "At Hi Ho, we combine UI design with strategic UX planning to create digital experiences that engage audiences and guide them smoothly from discovery to conversion.",
    ],
  },
];

const bottomRowServices: TopServiceCard[] = [
  {
    title: "Tone of Voice",
    paragraphs: [
      "A brand's tone of voice shapes how it speaks to the world. It defines the personality behind your messaging and ensures your communication feels consistent, authentic, and recognisable.",
      "Strong tone of voice builds connection with your audience. By understanding who you're speaking to and how they engage, we develop language that reflects your brand's character while making every message clear and meaningful.",
      "At Hi Ho, we craft tone of voice frameworks that guide how your brand communicates across websites, campaigns, and content, creating a consistent voice that strengthens recognition and builds trust.",
    ],
  },
  {
    title: "Copywriting",
    paragraphs: [
      "Copywriting shapes how your brand communicates across every channel. The right words clarify your message, capture attention, and help your audience quickly understand what makes your brand different.",
      "Consistent, well-crafted copy ensures your voice remains clear across websites, campaigns, social media, and advertising. When messaging is aligned, every touchpoint works together to strengthen your brand and guide customers toward action.",
      "At Hi Ho, we develop copy that supports strategy, builds connection, and ensures your message is understood wherever your audience encounters it.",
    ],
  },
];

export function ConceptDesignCreativeFunnelSection() {
  return (
    <section
      className="relative bg-[#b5adaa] pt-16 pb-20 font-sans text-[#231f20] md:pt-20 md:pb-28"
      aria-labelledby="concept-design-creative-funnel-heading"
    >
      <div className="site-content">
        <div className="mt-[clamp(1rem,6vw,10rem)] grid grid-cols-1 gap-8 lg:mt-[clamp(6rem,12vw,16rem)] lg:grid-cols-12 lg:gap-16 xl:gap-20">
          <div className="max-w-[650px] lg:col-span-7">
            <p className="text-sm font-medium tracking-wide text-[#231f20]/80 md:text-base">
              Creative development
            </p>
            <h2
              id="concept-design-creative-funnel-heading"
              className="mt-4 text-4xl leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl xl:text-7xl"
            >
              Strategic design that turns
              <span className={accent}> brand vision</span> into customer connection
            </h2>
          </div>

          <div className="lg:col-span-5">
            <p className={asteriskClassName} aria-hidden>
              *
            </p>
            <hr className="mt-4 max-w-full border-0 border-t border-[#231f20]/85" />
            <p className="mt-6 text-base leading-relaxed text-[#231f20]/95 md:text-lg">
              Designing a powerful brand experience starts
              with understanding who you are and who you’re
              speaking to. Our role is to translate that into
              clear, compelling design. We combine strategic
              thinking with thoughtful aesthetics to shape
              brands, interfaces, and digital experiences that
              attract attention, communicate purpose, and
              connect with the right audience so your brand
              doesn’t just look good, it resonates and drives
              meaningful engagement.

            </p>
          </div>
        </div>

        <div className="mt-16 border-t border-[#231f20]/40 pt-10 md:mt-20 md:pt-14">
          <div className="grid grid-cols-1 gap-x-12 gap-y-14 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-20 lg:gap-y-20">
            {topRowServices.map((service) => (
              <article key={service.title}>
                <p className={asteriskClassName} aria-hidden>
                  *
                </p>
                <h3 className={`mt-5 text-3xl font-medium leading-[1.05] ${accent}`}>
                  {service.title}
                </h3>
                <div className="mt-5 space-y-4 text-base leading-relaxed text-[#231f20]/60 md:text-lg">
                  {service.paragraphs.map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              </article>
            ))}

            <div className="self-start pt-12 md:pt-14">
              <h3 className="text-5xl font-medium leading-[0.9] tracking-tight sm:text-6xl lg:text-7xl">
                Connect through the power of <span className={accent}>your brand</span>
              </h3>
            </div>

            {bottomRowServices.map((service) => (
              <article key={service.title}>
                <p className={asteriskClassName} aria-hidden>
                  *
                </p>
                <h3 className={`mt-5 text-3xl font-medium leading-[1.05] ${accent}`}>
                  {service.title}
                </h3>
                <div className="mt-5 space-y-4 text-base leading-relaxed text-[#231f20]/60 md:text-lg">
                  {service.paragraphs.map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
